/**
 * LeadsChartsPanel — slicer + 6 charts panel for the Leads page (per spec).
 *
 * Charts:
 *  1. Total Leads (KPI card with click-through to list)
 *  2. Qualified Leads (KPI card with click-through to filtered list)
 *  3. Sales Pipeline race (horizontal bar)
 *  4. Lead by Source race (horizontal bar)
 *  5. Lead by Province (vertical bar)
 *  6. Lead by Team (grouped bar — Diamond=#c40303, Royal=#041f60)
 *
 * Slicers (per spec): kustomisasi tanggal, nama sales, Tahun Ajaran, team,
 * NPSN, Nama Sekolah, Status.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend,
} from "recharts";
import { Building, CheckCircle2, Filter, RefreshCw, TrendingUp, Users } from "lucide-react";
import { API } from "../../lib/api";
import { fmtN } from "../../lib/format";

const STATUS_COLORS = {
  "New Lead": "#1E5AA8", "Cold Prospect": "#6B7A90", "Prospective": "#2E86DE",
  "Hot Prospect": "#F39C12", "New Account Sign": "#27AE60",
  "Existing Account": "#0B2545", "Churn Account": "#8E44AD", "Lost Lead": "#E74C3C",
};

const TEAM_COLOR = { "DIAMOND": "#c40303", "ROYAL": "#041f60" };

const PIPELINE_ORDER = ["New Lead", "Cold Prospect", "Prospective", "Hot Prospect",
                        "New Account Sign", "Existing Account", "Churn Account", "Lost Lead"];

export default function LeadsChartsPanel({
  onFilterClick,
  onQualifiedClick,
  initialFilters = {},
  hideSlicers = false,
}) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [opts, setOpts] = useState({ nama_sales: [], tahun_ajaran: [], tim_sales: [] });
  const [filters, setFilters] = useState({
    sales: "", team: "", tahun_ajaran: "", npsn: "",
    nama_sekolah: "", status: "", date_from: "", date_to: "",
    ...initialFilters,
  });

  useEffect(() => {
    axios.get(`${API}/sales/filters`).then((r) => setOpts(r.data || {})).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    try {
      const r = await axios.get(`${API}/dashboard/leads-charts`, { params });
      setD(r.data);
    } catch { setD(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const reset = () => setFilters({ sales: "", team: "", tahun_ajaran: "", npsn: "",
                                    nama_sekolah: "", status: "", date_from: "", date_to: "" });

  // Pipeline race sorted by spec order
  const pipelineData = d ?
    PIPELINE_ORDER.map((s) => ({
      status: s,
      count: (d.pipeline || []).find((p) => p.status === s)?.count || 0,
    })) : [];

  // Team chart with custom colors
  const teamData = d ? (d.by_team || []).map((t) => ({
    team: t.team, count: t.count,
    color: TEAM_COLOR[String(t.team).toUpperCase()] || "#94a3b8",
  })) : [];

  return (
    <div data-testid="leads-charts-panel">
      {/* Slicers */}
      {!hideSlicers && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Filter size={14} color="var(--accent)" /><strong>Filter Leads</strong>
            <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "4px 10px" }}
              onClick={reset} data-testid="lcp-reset">Reset</button>
            <button className="btn btn-primary" style={{ padding: "4px 12px" }}
              onClick={load} disabled={loading} data-testid="lcp-apply">
              {loading ? <RefreshCw size={12} className="spin" /> : <Filter size={12} />} Terapkan
            </button>
          </div>
          <div className="card-b" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
            {[
              ["date_from", "Tanggal Dari", "date"],
              ["date_to", "Tanggal Sampai", "date"],
              ["sales", "Nama Sales", "select-sales"],
              ["tahun_ajaran", "Tahun Ajaran", "select-ta"],
              ["team", "Team (ROYAL/DIAMOND)", "select-team"],
              ["npsn", "NPSN", "text"],
              ["nama_sekolah", "Nama Sekolah", "text"],
              ["status", "Status", "select"],
            ].map(([k, label, type]) => {
              let options = null;
              if (type === "select") options = PIPELINE_ORDER;
              else if (type === "select-sales") options = opts.nama_sales || [];
              else if (type === "select-ta") options = opts.tahun_ajaran || [];
              else if (type === "select-team") options = (opts.tim_sales || []).filter((v) => v && v !== "—");
              return (
                <div className="field" key={k}>
                  <label style={{ fontSize: 11, color: "var(--muted)" }}>{label}</label>
                  {options ? (
                    <select className="select-input" value={filters[k]}
                      onChange={(e) => setFilters({ ...filters, [k]: e.target.value })}
                      data-testid={`lcp-f-${k}`}>
                      <option value="">Semua</option>
                      {options.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <input className="select-input" type={type === "date" ? "date" : "text"}
                      value={filters[k]} placeholder={type === "date" ? "" : "Semua"}
                      onChange={(e) => setFilters({ ...filters, [k]: e.target.value })}
                      data-testid={`lcp-f-${k}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* KPIs */}
      {d && (
        <div className="grid kpi-grid" style={{ marginTop: 14 }}>
          <button className="kpi-card" style={{ cursor: "pointer", textAlign: "left" }}
            onClick={() => onFilterClick?.(filters)} data-testid="lcp-total-card">
            <div className="kpi-icon"><Users size={20} /></div>
            <div className="label">TOTAL LEADS</div>
            <div className="value">{fmtN(d.summary.total_leads || 0)}</div>
            <div className="sub">Klik untuk lihat daftar</div>
          </button>
          <button className="kpi-card teachcast" style={{ cursor: "pointer", textAlign: "left" }}
            onClick={() => onQualifiedClick?.(filters)} data-testid="lcp-qualified-card">
            <div className="kpi-icon"><CheckCircle2 size={20} /></div>
            <div className="label">QUALIFIED LEADS</div>
            <div className="value">{fmtN(d.summary.qualified_leads || 0)}</div>
            <div className="sub">Sekolah nasional · ≥50 siswa</div>
          </button>
        </div>
      )}

      {/* Sales Pipeline Race */}
      {d && (
        <div className="grid row-split" style={{ marginTop: 14 }}>
          <div className="card" data-testid="lcp-ch-pipeline">
            <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <TrendingUp size={14} color="var(--accent)" />
              <strong>Sales Pipeline</strong>
              <span className="badge" style={{ marginLeft: "auto" }}>Race</span>
            </div>
            <div className="card-b" style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <BarChart data={pipelineData} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="status" width={130} fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {pipelineData.map((p, i) => <Cell key={i} fill={STATUS_COLORS[p.status] || "#94a3b8"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Lead by Source race */}
          <div className="card" data-testid="lcp-ch-source">
            <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Building size={14} color="var(--accent)" />
              <strong>Lead by Source</strong>
              <span className="badge" style={{ marginLeft: "auto" }}>Race</span>
            </div>
            <div className="card-b" style={{ height: 320 }}>
              {(!d.by_source || d.by_source.length === 0) ?
                <div className="empty">Belum ada data source.</div> :
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <BarChart data={d.by_source.slice(0, 10)} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="source" width={130} fontSize={11} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#2E86DE" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              }
            </div>
          </div>
        </div>
      )}

      {/* Lead by Province + Lead by Team */}
      {d && (
        <div className="grid row-split" style={{ marginTop: 14 }}>
          <div className="card" data-testid="lcp-ch-province">
            <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <strong>Lead by Province</strong>
              <span className="badge" style={{ marginLeft: "auto" }}>Top 15</span>
            </div>
            <div className="card-b" style={{ height: 320 }}>
              {(!d.by_province || d.by_province.length === 0) ?
                <div className="empty">Belum ada propinsi tercatat.</div> :
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <BarChart data={d.by_province}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="propinsi" fontSize={10} angle={-30} textAnchor="end" height={80} interval={0} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#1E5AA8" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              }
            </div>
          </div>

          <div className="card" data-testid="lcp-ch-team">
            <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <strong>Lead by Team</strong>
              <span className="badge" style={{ marginLeft: "auto", background: "#c40303", color: "#fff" }}>Diamond</span>
              <span className="badge" style={{ background: "#041f60", color: "#fff" }}>Royal</span>
            </div>
            <div className="card-b" style={{ height: 320 }}>
              {teamData.length === 0 ? <div className="empty">Belum ada team assignment.</div> :
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <BarChart data={teamData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="team" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {teamData.map((t, i) => <Cell key={i} fill={t.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
